import TokenService from '@/services/storage.service'
import axios from 'axios'
import { CHANGE_PASSWORD_PATH } from '@/auth/temporaryPassword'

export const LOGIN_PATH = '/'

const PUBLIC_PATHS = [LOGIN_PATH, '/error', '/login', CHANGE_PASSWORD_PATH]

let isHandlingSessionExpiry = false
let sessionMarkedExpired = false

export class SessionExpiredError extends Error {
  constructor() {
    super('SESSION_EXPIRED')
    this.name = 'SessionExpiredError'
  }
}

export function isAuthSessionExpired() {
  return sessionMarkedExpired
}

export function resetAuthSessionExpired() {
  sessionMarkedExpired = false
  isHandlingSessionExpiry = false
}

export function clearAuthSession() {
  sessionMarkedExpired = true
  TokenService.removeToken()
  TokenService.removeRefreshToken()
  TokenService.removeAdminData()
  delete axios.defaults.headers.common.Authorization
  delete axios.defaults.headers.common['Content-Type']

  import('@/store').then(({ default: store }) => {
    store.commit('CLEAR_AUTH_SESSION')
  })
}

export function hasStoredAccessToken() {
  return Boolean(TokenService.getToken())
}

export function isSessionExpiredResponse(error) {
  const status = error?.response?.status
  return status === 401 || status === 419
}

export function canAttemptTokenRefresh(error) {
  const status = error?.response?.status
  return (status === 401 || status === 419) && hasStoredAccessToken()
}

export function shouldForceLogoutOnApiError(error) {
  if (!isSessionExpiredResponse(error)) {
    return false
  }

  const requestUrl = error?.config?.url || ''
  const isAuthRequest =
    requestUrl.includes('auth/login') || requestUrl.includes('auth/refresh') || requestUrl.includes('auth/change-forced-password') || requestUrl.includes('users/logout')

  return !isAuthRequest && hasStoredAccessToken()
}

export function isPublicRoutePath(path) {
  return PUBLIC_PATHS.includes(path)
}

function buildLoginRedirectQuery(redirectFrom) {
  if (!redirectFrom || redirectFrom === LOGIN_PATH || redirectFrom === '/error') {
    return {}
  }

  return { redirect: redirectFrom }
}

export function abortNavigationToLogin(next, redirectFrom = null) {
  clearAuthSession()

  next({
    path: LOGIN_PATH,
    query: buildLoginRedirectQuery(redirectFrom),
    replace: true
  })
}

export function forceLogoutToLogin(options = {}) {
  const { redirect = true, redirectFrom = null } = options

  if (isHandlingSessionExpiry) {
    return
  }

  isHandlingSessionExpiry = true
  clearAuthSession()

  if (!redirect) {
    isHandlingSessionExpiry = false
    return
  }

  import('@/router')
    .then((module) => {
      const router = module.default
      const currentRoute = router.currentRoute.value
      const currentPath = currentRoute.path

      if (isPublicRoutePath(currentPath)) {
        return router.replace({ path: LOGIN_PATH, query: {} })
      }

      const preservedRedirect = redirectFrom || currentRoute.fullPath

      return router.replace({
        path: LOGIN_PATH,
        query: buildLoginRedirectQuery(preservedRedirect)
      })
    })
    .finally(() => {
      isHandlingSessionExpiry = false
    })
}

export { PUBLIC_PATHS }
